/**
 * LabelPreviewModal - Displays a preview of the label when Simulate Printer is ON
 * Shows an actual-size preview (4in x 3in) of what the label would look like when printed.
 */
import React, { useEffect, useState, useRef } from 'react';
import { X, Printer, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

// Original label dimensions (4in x 3in at 96dpi)
const LABEL_WIDTH = 384;
const LABEL_HEIGHT = 288;

interface LabelPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  labelHtml: string | string[];
  copies?: number;
}

const LabelPreviewModal: React.FC<LabelPreviewModalProps> = ({
  isOpen,
  onClose,
  labelHtml,
  copies = 1,
}) => {
  const { t } = useLanguage();
  const [iframeKey, setIframeKey] = useState(0);
  const [scale, setScale] = useState(1);
  const [pageIndex, setPageIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const pages = Array.isArray(labelHtml) ? labelHtml : [labelHtml];
  const currentHtml = pages[pageIndex] || '';

  // Reset page and refresh iframe when modal opens or content changes
  useEffect(() => {
    if (isOpen) {
      setPageIndex(0);
      setIframeKey(prev => prev + 1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && currentHtml) setIframeKey(prev => prev + 1);
  }, [pageIndex]);

  // Calculate scale based on container width
  useEffect(() => {
    if (!isOpen) return;

    const calculateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth - 16; // Account for padding
        const newScale = Math.min(1, containerWidth / LABEL_WIDTH);
        setScale(newScale);
      }
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-[500px] w-full mx-2 sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b">
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-blue-600" />
            <h2 className="text-base sm:text-lg font-semibold">
              {t('print.previewTitle') || 'Label Preview'}
              <span className="ml-2 text-xs sm:text-sm font-normal text-gray-500">
                (TEST MODE)
              </span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4">
          <p className="text-sm text-gray-600 mb-3">
            {t('print.previewDescription') || 'This is what the label would look like when printed.'}
          </p>
          <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-50 rounded-lg">
            <Printer className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              {copies} {copies === 1 ? (t('print.copy') || 'copy') : (t('print.copies') || 'copies')} {t('print.willBePrinted') || 'will be printed'}
            </span>
          </div>

          {/* Label Preview Container - Scales down on mobile, full size on desktop */}
          <div className="flex justify-center" ref={containerRef}>
            <div
              className="border-2 border-dashed border-gray-300 bg-gray-50 p-2 rounded overflow-hidden"
              style={{
                width: LABEL_WIDTH * scale + 16,
                height: LABEL_HEIGHT * scale + 16,
              }}
            >
              {/* Iframe at original size, scaled down via CSS transform */}
              <div
                className="bg-white shadow-md origin-top-left"
                style={{
                  width: LABEL_WIDTH,
                  height: LABEL_HEIGHT,
                  transform: `scale(${scale})`,
                }}
              >
                <iframe
                  key={iframeKey}
                  srcDoc={currentHtml}
                  title="Label Preview"
                  style={{
                    width: LABEL_WIDTH,
                    height: LABEL_HEIGHT,
                    border: 'none',
                  }}
                  sandbox="allow-same-origin allow-scripts"
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-400 text-center mt-2">
            4" x 3" (actual size at 96 DPI)
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50 rounded-b-lg">
          {pages.length > 1 ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPageIndex(i => i - 1)}
                disabled={pageIndex === 0}
                className="p-1.5 rounded-full hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                {pageIndex + 1} / {pages.length}
              </span>
              <button
                onClick={() => setPageIndex(i => i + 1)}
                disabled={pageIndex === pages.length - 1}
                className="p-1.5 rounded-full hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : <div />}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('common.continue') || 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabelPreviewModal;
